import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, Loader2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError } from '@/lib/api';
import {
  fetchReceivedReferralAttachment,
  type ReceivedReferralAttachment,
  type ReceivedReferralAttachmentFile,
} from '@/hooks/useReferrals';
import { attachmentViewKind, base64ToBlob, formatAttachmentBytes } from '@/lib/referral/attachmentViewer';

/**
 * The received MEDBIN for one <Reference> row (C5): status, name, type and
 * size, and 'Vis bilag' which opens the viewer (image/pdf/text inline,
 * otherwise download).
 */
export function ReceivedAttachmentFile({
  referralId,
  attachment,
  loading,
}: {
  referralId: string;
  attachment: ReceivedReferralAttachment | undefined;
  loading: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <Loader2 className="h-4 w-4 animate-spin text-ds-muted" aria-label={t('common.loading', 'Indlæser')} />;
  }
  if (!attachment || !attachment.received || !attachment.exchange_id) {
    return (
      <div className="text-sm text-ds-muted" data-testid="referral-attachment-missing">
        {t('referrals.medcom_attachment_not_received')}
      </div>
    );
  }
  const locale = i18n.language === 'en' ? 'en-US' : 'da-DK';
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-sm font-medium text-ds-teal hover:text-ds-teal hover:underline"
        data-testid="referral-attachment-open"
      >
        <Eye className="h-4 w-4" />
        {t('referrals.medcom_attachment_open')}
      </button>
      <div className="break-all text-xs text-ds-muted">
        {attachment.file_name} · {attachment.mime_type} · {formatAttachmentBytes(attachment.actual_size, locale)}
      </div>
      {attachment.size_matches === false && (
        <div className="text-xs font-medium text-ds-red-ink">
          {t('referrals.medcom_attachment_size_mismatch', {
            expected: attachment.original_object_size ?? '—',
            actual: attachment.actual_size ?? '—',
          })}
        </div>
      )}
      {open && (
        <AttachmentViewer
          referralId={referralId}
          exchangeId={attachment.exchange_id}
          objectIdentifier={attachment.object_identifier}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function AttachmentViewer({
  referralId,
  exchangeId,
  objectIdentifier,
  onClose,
}: {
  referralId: string;
  exchangeId: string;
  objectIdentifier: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState<ReceivedReferralAttachmentFile | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // The browser could not decode the bytes (e.g. a damaged JPEG). The file is
  // still offered for download, byte for byte as received.
  const [undecodable, setUndecodable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchReceivedReferralAttachment(referralId, exchangeId, objectIdentifier)
      .then((f) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(base64ToBlob(f.base64, f.mime_type));
        setFile(f);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(err instanceof ApiCallError ? err.message : t('referrals.medcom_attachment_load_failed'));
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [referralId, exchangeId, objectIdentifier, t]);

  const kind = attachmentViewKind(file?.mime_type);
  const locale = i18n.language === 'en' ? 'en-US' : 'da-DK';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim p-4" role="dialog" aria-modal="true">
      <div className="flex h-full max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-ds-card shadow-pop">
        <div className="flex items-center justify-between gap-3 border-b border-ds-line px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ds-text">
              {file?.file_name ?? t('referrals.medcom_attachment_open')}
            </div>
            {file && (
              <div className="text-xs text-ds-muted">
                {file.mime_type} · {formatAttachmentBytes(file.size, locale)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {file && url && (
              <a
                href={url}
                download={file.file_name}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-ds-teal hover:bg-ds-teal-tint"
              >
                <Download className="h-4 w-4" />
                {t('referrals.medcom_attachment_download')}
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-ds-muted hover:bg-ds-row-hover"
              aria-label={t('common.close', 'Luk')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-ds-sunk p-3">
          {!file && !failed && (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
            </div>
          )}
          {failed && (
            <div className="p-4 text-sm text-ds-red-ink">
              {t('referrals.medcom_attachment_load_failed')}
            </div>
          )}
          {file && url && kind === 'image' && !undecodable && (
            <img
              src={url}
              alt={file.file_name}
              className="mx-auto max-h-full max-w-full object-contain"
              onError={() => setUndecodable(true)}
              data-testid="medcom-attachment-image"
            />
          )}
          {file && url && kind === 'image' && undecodable && (
            <div role="alert" className="p-4 text-sm text-ds-amber-ink" data-testid="medcom-attachment-undecodable">
              {t('referrals.medcom_attachment_undecodable', {
                type: file.mime_type,
                size: formatAttachmentBytes(file.size, locale),
                defaultValue:
                  'Billedet kan ikke vises: indholdet er ikke et gyldigt billede af typen {{type}}. Filen er modtaget uændret ({{size}}) og kan hentes.',
              })}
            </div>
          )}
          {file && url && (kind === 'pdf' || kind === 'text') && (
            <iframe src={url} title={file.file_name} className="h-full min-h-[60vh] w-full rounded bg-ds-card" />
          )}
          {file && url && kind === 'download' && (
            <div className="p-4 text-sm text-ds-text">
              {t('referrals.medcom_attachment_no_preview')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
